import exifr from "exifr";
import heic2any from "heic2any";

export const MAX_CHAT_IMAGES = 3;
export const MAX_CHAT_IMAGE_BYTES = 5 * 1024 * 1024;
export const MAX_CHAT_IMAGES_BYTES = 12 * 1024 * 1024;

export type ChatImageMimeType = "image/jpeg" | "image/png" | "image/webp";

export interface ChatImageData {
  dataUrl: string;
  mimeType: ChatImageMimeType;
}

export class ChatImageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ChatImageError";
  }
}

type DetectedImageType = ChatImageMimeType | "image/heic" | null;

const DATA_URI_RE = /^data:([^;,]+);base64,(.*)$/i;
const HEIC_BRANDS = new Set(["heic", "heix", "hevc", "hevx", "mif1", "msf1"]);

const normaliseMimeType = (mimeType?: string | null): DetectedImageType => {
  switch (mimeType?.toLowerCase().trim()) {
    case "image/jpeg":
    case "image/jpg":
      return "image/jpeg";
    case "image/png":
      return "image/png";
    case "image/webp":
      return "image/webp";
    case "image/heic":
    case "image/heif":
      return "image/heic";
    default:
      return null;
  }
};

const extensionMimeType = (filename?: string | null): DetectedImageType => {
  const extension = filename?.split(/[?#]/)[0].split(".").pop()?.toLowerCase();
  if (extension === "jpg" || extension === "jpeg") return "image/jpeg";
  if (extension === "png") return "image/png";
  if (extension === "webp") return "image/webp";
  if (extension === "heic" || extension === "heif") return "image/heic";
  return null;
};

const rawBase64 = (data: string): string => data.match(DATA_URI_RE)?.[2] ?? data;

const bytesFromBase64 = (data: string): Uint8Array => {
  const raw = rawBase64(data).replace(/\s/g, "");
  try {
    const binary = atob(raw);
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
  } catch {
    throw new ChatImageError("This image file is invalid. Choose another photo.");
  }
};

export const decodedImageBytes = (data: string): number => bytesFromBase64(data).byteLength;

const ascii = (bytes: Uint8Array, start: number, length: number): string =>
  String.fromCharCode(...bytes.slice(start, start + length));

export const detectImageType = ({
  data,
  mimeType,
  filename,
}: {
  data: string;
  mimeType?: string | null;
  filename?: string | null;
}): DetectedImageType => {
  const bytes = bytesFromBase64(data);
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return "image/jpeg";
  }
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    ascii(bytes, 1, 3) === "PNG" &&
    bytes[4] === 0x0d &&
    bytes[5] === 0x0a &&
    bytes[6] === 0x1a &&
    bytes[7] === 0x0a
  ) {
    return "image/png";
  }
  if (bytes.length >= 12 && ascii(bytes, 0, 4) === "RIFF" && ascii(bytes, 8, 4) === "WEBP") {
    return "image/webp";
  }
  if (bytes.length >= 12 && ascii(bytes, 4, 4) === "ftyp" && HEIC_BRANDS.has(ascii(bytes, 8, 4))) {
    return "image/heic";
  }
  // JPEG, PNG, and WebP have unambiguous magic bytes. Do not trust a declared
  // MIME type for those formats: the backend verifies the bytes too. HEIC/HEIF
  // is the exception because several valid ISO-BMFF brand variants exist, so
  // retain the picker MIME type / filename as an additional detection signal.
  const declaredType = normaliseMimeType(mimeType) ?? extensionMimeType(filename);
  return declaredType === "image/heic" ? declaredType : null;
};

const blobFromBase64 = (data: string, type: string): Blob =>
  new Blob([bytesFromBase64(data)], { type });

export const dataUrlFromBlob = async (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(new ChatImageError("This image file is invalid. Choose another photo."));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(blob);
  });

const canvasBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new ChatImageError("Couldn't resize this photo."))),
      "image/jpeg",
      quality,
    );
  });

const orientedJpeg = async (source: Blob, orientation?: number): Promise<Blob> => {
  const bitmap = await createImageBitmap(source);
  const rotation = orientation ? exifr.rotations[orientation] : undefined;
  const width = rotation?.dimensionSwapped ? bitmap.height : bitmap.width;
  const height = rotation?.dimensionSwapped ? bitmap.width : bitmap.height;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new ChatImageError("Couldn't resize this photo.");

  context.translate(width / 2, height / 2);
  if (rotation) {
    context.rotate(rotation.rad);
    context.scale(rotation.scaleX, 1);
  }
  context.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);
  bitmap.close();
  return canvasBlob(canvas, 0.88);
};

const shrinkJpegToLimit = async (source: Blob, orientation?: number): Promise<Blob> => {
  let jpeg = await orientedJpeg(source, orientation);
  if (jpeg.size <= MAX_CHAT_IMAGE_BYTES) return jpeg;

  const bitmap = await createImageBitmap(jpeg);
  let width = bitmap.width;
  let height = bitmap.height;
  bitmap.close();
  let quality = 0.84;

  for (let attempt = 0; attempt < 8 && jpeg.size > MAX_CHAT_IMAGE_BYTES; attempt += 1) {
    width = Math.max(1, Math.floor(width * 0.8));
    height = Math.max(1, Math.floor(height * 0.8));
    const image = await createImageBitmap(jpeg);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new ChatImageError("Couldn't resize this photo.");
    context.drawImage(image, 0, 0, width, height);
    image.close();
    jpeg = await canvasBlob(canvas, quality);
    quality = Math.max(0.7, quality - 0.02);
  }

  if (jpeg.size > MAX_CHAT_IMAGE_BYTES) {
    throw new ChatImageError("This photo could not be resized below 5 MB. Choose another image.");
  }
  return jpeg;
};

export const normaliseChatImage = async ({
  data,
  mimeType,
  filename,
}: {
  data: string;
  mimeType?: string | null;
  filename?: string | null;
}): Promise<ChatImageData> => {
  const detected = detectImageType({ data, mimeType, filename });
  if (!detected) {
    throw new ChatImageError("Choose a JPEG, PNG, WebP, HEIC, or HEIF image.");
  }

  if (detected === "image/heic") {
    try {
      const heic = blobFromBase64(data, "image/heic");
      const orientation = await exifr.orientation(heic).catch(() => undefined);
      const converted = await heic2any({ blob: heic, toType: "image/jpeg", quality: 0.88 });
      const jpeg = await shrinkJpegToLimit(
        Array.isArray(converted) ? converted[0] : converted,
        orientation,
      );
      const dataUrl = await dataUrlFromBlob(jpeg);
      if (detectImageType({ data: dataUrl, mimeType: "image/jpeg" }) !== "image/jpeg") {
        throw new ChatImageError("Couldn't convert this HEIC photo.");
      }
      return { dataUrl, mimeType: "image/jpeg" };
    } catch (error) {
      if (error instanceof ChatImageError) throw error;
      throw new ChatImageError("Couldn't convert this HEIC photo. Choose another image.");
    }
  }

  const byteCount = decodedImageBytes(data);
  if (byteCount > MAX_CHAT_IMAGE_BYTES) {
    try {
      const source = blobFromBase64(data, detected);
      // createImageBitmap applies embedded JPEG orientation by default, while
      // native picker results have already been corrected by Camera.
      const jpeg = await shrinkJpegToLimit(source);
      const dataUrl = await dataUrlFromBlob(jpeg);
      if (detectImageType({ data: dataUrl, mimeType: "image/jpeg" }) !== "image/jpeg") {
        throw new ChatImageError("Couldn't resize this photo.");
      }
      return { dataUrl, mimeType: "image/jpeg" };
    } catch (error) {
      if (error instanceof ChatImageError) throw error;
      throw new ChatImageError("Couldn't resize this photo. Choose another image.");
    }
  }
  return { dataUrl: `data:${detected};base64,${rawBase64(data)}`, mimeType: detected };
};

export const validateChatImageBatch = (images: ChatImageData[]): void => {
  if (images.length > MAX_CHAT_IMAGES) {
    throw new ChatImageError("You can attach up to 3 images.");
  }
  const sizes = images.map((image) => decodedImageBytes(image.dataUrl));
  if (sizes.some((size) => size > MAX_CHAT_IMAGE_BYTES)) {
    throw new ChatImageError("Each image must be 5 MB or smaller.");
  }
  const total = sizes.reduce((sum, size) => sum + size, 0);
  if (total > MAX_CHAT_IMAGES_BYTES) {
    throw new ChatImageError("Your attached images must total 12 MB or less.");
  }
};

/** Append against the latest atom value so a picker result can never replace
 * an attachment selected immediately before it. */
export const appendChatImage = (images: ChatImageData[], image: ChatImageData): ChatImageData[] => {
  const next = [...images, image];
  validateChatImageBatch(next);
  return next;
};

/** Batch form of appendChatImage — same "append against latest atom value" contract,
 * validated once against the combined result. */
export const appendChatImages = (
  images: ChatImageData[],
  toAdd: ChatImageData[],
): ChatImageData[] => {
  const next = [...images, ...toAdd];
  validateChatImageBatch(next);
  return next;
};
